import React, { useState, useRef, useMemo } from "react";
import { Modal } from "@talentbait/shared-components";
import { XIcon } from "../../../../Components/Icons";

import {
  EditCampaignDataModalWrapper,
  Header,
  TitleContainer,
  Title,
  CloseButton,
  Content,
  ContentContainer,
  FilterButtonsContainer,
  FilterButton,
  UploadButtonContainer,
  UploadButton,
  modalStyles
} from "./PopupPreview.styled";
import { useTranslation } from "react-i18next";
import MediaGrid from "../media-grid/MediaGrid";
import { ActionsGlobalMediaLibrary } from "../../../../Actions/ActionsGlobalMediaLibrary/ActionsGlobalMediaLibrary";
import { prepareCompanyImageFolderName } from "../../../../utils/Helpers";
import { useUploadInput } from "../../../../hooks/useUploadInput";
import { withRefluxStore } from "../../../../Stores";
import { Store__System } from "@talentbait/shared-components";
import GlobalMediaLibraryStore from "../../GlobalMediaLibraryStore";
import logger from "../../../../utils/LoggerFrontend";

const PopupPreview = ({
  isOpen,
  setIsOpen,
  companyId,
  companyName,
  campaignData,
  emitNewImageSelection = null,
  onlyShowLogos = false,
  organization,
  isOldFlow = false
}) => {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState("all");
  const fileInputRef = useRef(null);

  const { folderName } = useMemo(() => {
    try {
      const result = prepareCompanyImageFolderName(organization?.id, companyId);
      logger.log("PopupPreview:folder:prepared", "Folder prepared", {
        folderName: result.folderName,
        companyId
      });
      return result;
    } catch (error) {
      logger.log("error:PopupPreview:folder:failed", "Folder preparation failed", {
        error: error?.message || error,
        companyId
      });
      return { folderName: "" };
    }
  }, [organization?.id, companyId]);

  const cloudinaryContext = useMemo(() => {
    const context = `orgaId=${organization?.id}|source=template-media-library|companyId=${companyId}`;
    logger.log("PopupPreview:context:created", "Cloudinary context created", {
      context,
      companyId
    });
    return context;
  }, [organization?.id, companyId]);

  const emitNewImageUpload = (newAsset) => {
    if (newAsset) {
      const isLogoUpload = onlyShowLogos || activeFilter === "logos";
      const action = isLogoUpload ? "newlyUploadedLogos" : "newlyUploadedImages";

      logger.log("PopupPreview:upload:asset", "Asset uploaded", {
        isLogoUpload,
        action,
        companyId
      });

      if (isLogoUpload) {
        ActionsGlobalMediaLibrary.newlyUploadedLogos([newAsset]);
      } else {
        ActionsGlobalMediaLibrary.newlyUploadedImages([newAsset]);
      }
    }
  };

  const { handleInputChange } = useUploadInput({
    cloudinaryContext,
    folderName,
    emitNewImageUpload,
    isLogo: onlyShowLogos || activeFilter === "logos"
  });

  const handleUploadClick = () => {
    logger.log("PopupPreview:upload:clicked", "Upload clicked", {
      onlyShowLogos,
      activeFilter,
      companyId
    });
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      logger.log("PopupPreview:files:selected", "Files selected", {
        fileCount: files.length,
        onlyShowLogos,
        activeFilter,
        companyId
      });

      ActionsGlobalMediaLibrary.startUpload(files.length);
      handleInputChange(event);
    }
    event.target.value = "";
  };

  const handleFilterChange = (filterType) => {
    logger.log("PopupPreview:filter:changed", "Filter changed", {
      previousFilter: activeFilter,
      newFilter: filterType,
      companyId
    });
    setActiveFilter(filterType);
  };

  const handleCloseModal = () => {
    logger.log("PopupPreview:modal:closed", "Modal closed", {
      onlyShowLogos,
      activeFilter,
      companyId
    });

    setIsOpen();
  };

  const filters = {
    contentType: onlyShowLogos ? "logos" : activeFilter === "logos" ? "logos" : "all",
    mediaType:
      onlyShowLogos ||
      activeFilter === "all" ||
      activeFilter === "shutterstock" ||
      activeFilter === "logos"
        ? []
        : [activeFilter],
    ratio: [],
    showKPIs: false,
    showShutterstock: onlyShowLogos ? false : activeFilter === "shutterstock"
  };

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen} styles={modalStyles}>
      <EditCampaignDataModalWrapper>
        <Header>
          <TitleContainer>
            <Title>{t("Preview")}</Title>
            <CloseButton onClick={handleCloseModal}>
              <XIcon color="var(--primary-white)" />
            </CloseButton>
          </TitleContainer>
        </Header>
        <Content>
          <ContentContainer>
            {!onlyShowLogos && (
              <FilterButtonsContainer>
                <FilterButton
                  className={activeFilter === "all" ? "active" : ""}
                  onClick={() => handleFilterChange("all")}
                >
                  {t("All")}
                </FilterButton>
                <FilterButton
                  className={activeFilter === "videos" ? "active" : ""}
                  onClick={() => handleFilterChange("videos")}
                >
                  {t("Videos")}
                </FilterButton>
                <FilterButton
                  className={activeFilter === "images" ? "active" : ""}
                  onClick={() => handleFilterChange("images")}
                >
                  {t("Images")}
                </FilterButton>
                <FilterButton
                  className={activeFilter === "logos" ? "active" : ""}
                  onClick={() => handleFilterChange("logos")}
                >
                  {t("Logos")}
                </FilterButton>
                <FilterButton
                  className={activeFilter === "shutterstock" ? "active" : ""}
                  onClick={() => handleFilterChange("shutterstock")}
                >
                  Shutterstock
                </FilterButton>
              </FilterButtonsContainer>
            )}

            {isOldFlow && (
              <UploadButtonContainer>
                <UploadButton onClick={handleUploadClick}>
                  <span>+ {t("Upload new asset")}</span>
                </UploadButton>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  style={{ display: "none" }}
                  onChange={handleFileSelect}
                />
              </UploadButtonContainer>
            )}

            <MediaGrid
              filters={filters}
              companyId={companyId}
              companyName={companyName}
              isPopupPreview={true}
              emitNewImageSelection={emitNewImageSelection}
              campaignData={campaignData}
            />
          </ContentContainer>
        </Content>
      </EditCampaignDataModalWrapper>
    </Modal>
  );
};

export default withRefluxStore(
  PopupPreview,
  [Store__System, GlobalMediaLibraryStore],
  ["organization"]
);
